import { DocumentId, StrapiCreate, StrapiUpdate } from '../../types/strapi/strapi'
import { createApi } from '@reduxjs/toolkit/query/react'
import getCustomFetchBase from '../custom-fetch-base'
import Strapi, { EndpointQueryParameters, StrapiSingle } from '../../types/strapi/strapi'
import getQuery from '../../helpers/api/get-query'
import Shipment from '../../types/api/shipment'
import ExecuteResult from '../../types/api/shipments/execute-result'
import CreateScheduleJobsResult from '../../types/api/shipments/create-schedule-jobs-result'
import {
    ShipmentPeriodStatsQueryParams,
    ShipmentPeriodStatsResponse,
} from '../../types/api/shipment-period-stats'
import { SHIPMENT_BLANK_TYPE_PATH } from '../../types/api/shipment-blank-type'

export const shipmentsApi = createApi({
    reducerPath: 'shipmentsApi',
    baseQuery: getCustomFetchBase({ baseUrl: process.env.REACT_APP_API_URL }),
    tagTypes: ['Shipments'],
    endpoints: builder => ({
        getShipments: builder.query<Strapi<Shipment>, EndpointQueryParameters | void>({
            query: getQuery('/shipments/my'),
            providesTags: ['Shipments'],
        }),
        getMyCuratorShipments: builder.query<Strapi<Shipment>, EndpointQueryParameters | void>({
            query: getQuery('/shipments/my-curator-shipments'),
            providesTags: ['Shipments'],
        }),
        getMyShipmentsPeriodStats: builder.query<
            ShipmentPeriodStatsResponse,
            ShipmentPeriodStatsQueryParams
        >({
            query: params =>
                getQuery('/shipments/my/period-stats')(params as EndpointQueryParameters),
        }),
        getShipmentByDocId: builder.query<
            StrapiSingle<Shipment>,
            [string, EndpointQueryParameters] | [string]
        >({
            query: ([docId, params]) => getQuery(`/shipments/${docId}`)(params),
        }),
        createShipment: builder.mutation<StrapiSingle<Shipment>, StrapiCreate<Shipment>>({
            query: body => ({
                url: '/shipments?populate=*', // TODO: это временное решение, необходимо реализовать более общее
                method: 'POST',
                body,
            }),
            // invalidatesTags: ['Shipments'],
        }),
        updateShipment: builder.mutation<StrapiSingle<Shipment>, StrapiUpdate<Shipment>>({
            query: ({ docId, body, queryParams }) => ({
                url: getQuery(`/shipments/${docId}`)(queryParams),
                method: 'PUT',
                body,
            }),
            // invalidatesTags: ['Shipments'],
        }),
        deleteShipment: builder.mutation<void, string>({
            query: docId => ({ url: `/shipments/${docId}`, method: 'DELETE' }),
            invalidatesTags: ['Shipments'],
        }),
        executeShipment: builder.mutation<ExecuteResult, string>({
            query: docId => ({ url: `/shipments/${docId}/execute`, method: 'POST' }),
            invalidatesTags: ['Shipments'],
        }),
        createShipmentScheduleJobs: builder.mutation<{ data: CreateScheduleJobsResult }, string>({
            query: docId => ({
                url: `/shipments/${docId}/create-schedule-jobs`,
                method: 'POST',
            }),
            invalidatesTags: ['Shipments'],
        }),
        createAssignmentApprovalTask: builder.mutation<{ data: unknown }, string>({
            query: docId => ({
                url: `/shipments/${docId}/create-assignment-approval-task`,
                method: 'POST',
            }),
            invalidatesTags: ['Shipments'],
        }),
        downloadShipmentBlankJpeg: builder.query<Blob, { docId: string; previewIndex?: number }>({
            query: ({ docId, previewIndex }) => ({
                url: `/shipments/${docId}/${SHIPMENT_BLANK_TYPE_PATH.client}${
                    previewIndex === undefined ? '' : `?previewIndex=${previewIndex}`
                }`,
                responseHandler: resp => (resp.status === 200 ? resp.blob() : resp.json()),
            }),
            keepUnusedDataFor: 0,
        }),
        downloadProductionBlankJpeg: builder.query<Blob, { docId: string; previewIndex?: number }>({
            query: ({ docId, previewIndex }) => ({
                url: `/shipments/${docId}/${SHIPMENT_BLANK_TYPE_PATH.production}${
                    previewIndex === undefined ? '' : `?previewIndex=${previewIndex}`
                }`,
                responseHandler: resp => (resp.status === 200 ? resp.blob() : resp.json()),
            }),
            keepUnusedDataFor: 0,
        }),
        executeShipmentMaterial: builder.mutation<any, DocumentId>({
            query: docId => ({
                url: `/shipments/${docId}/order-materials`,
                method: 'POST',
            }),
        }),
        setShipmentToolingInclusion: builder.mutation<
            StrapiSingle<Shipment>,
            {
                docId: string
                kind: 'stamp' | 'forms'
                included: boolean
                queryParams?: EndpointQueryParameters
            }
        >({
            query: ({ docId, kind, included, queryParams }) => ({
                url: getQuery(`/shipments/${docId}/tooling-inclusion`)(queryParams),
                method: 'PUT',
                body: { kind, included },
            }),
        }),
        updateAndCalculateShipment: builder.mutation<
            StrapiSingle<Shipment>,
            StrapiUpdate<Shipment>
        >({
            query: ({ docId, body, queryParams }) => ({
                url: getQuery(`/shipments/${docId}/update-and-calculate`)(queryParams),
                method: 'PUT',
                body,
            }),
        }),
        launchPendingMaterials: builder.mutation<Shipment, StrapiUpdate<Shipment>>({
            query: ({ docId, body, queryParams }) => ({
                url: getQuery(`/shipments/${docId}/launch-pending-materials`)(queryParams),
                method: 'POST',
                body,
            }),
        }),
    }),
})

export const {
    useGetShipmentsQuery,
    useGetMyCuratorShipmentsQuery,
    useGetMyShipmentsPeriodStatsQuery,
    useGetShipmentByDocIdQuery,
    useLazyGetShipmentByDocIdQuery,
    useCreateShipmentMutation,
    useUpdateShipmentMutation,
    useDeleteShipmentMutation,
    useExecuteShipmentMutation,
    useCreateShipmentScheduleJobsMutation,
    useCreateAssignmentApprovalTaskMutation,
    useLazyDownloadShipmentBlankJpegQuery,
    useLazyDownloadProductionBlankJpegQuery,
    useExecuteShipmentMaterialMutation,
    useSetShipmentToolingInclusionMutation,
    useUpdateAndCalculateShipmentMutation,
    useLaunchPendingMaterialsMutation,
} = shipmentsApi
